import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/lib/i18n";
import { ChevronDown } from "lucide-react";

const GROUPS = [
  {
    label: "Profile",
    links: [
      { href: "#journey", label: "Journey" },
      { href: "#about", label: "About" },
      { href: "#experience", label: "Experience" },
      { href: "#personality", label: "Personality" },
    ],
  },
  {
    label: "Work",
    links: [
      { href: "#projects", label: "Projects" },
      { href: "#deliverables", label: "Deliverables" },
      { href: "#ai-methodology", label: "AI method" },
      { href: "#toolkit", label: "Toolkit" },
    ],
  },
  {
    label: "Proof",
    links: [
      { href: "#testimonials", label: "Testimonials" },
    ],
  },
];
const LINKS = GROUPS.flatMap((g) => g.links);

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(null);
  const [active, setActive] = useState(null);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const { lang, setLang, t } = useLang();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => {
    if (!isHome) return setActive(null);
    const on = () => {
      const y = window.scrollY + 120;
      let cur = null;
      GROUPS.forEach((g) =>
        g.links.forEach((l) => {
          const el = document.getElementById(l.href.slice(1));
          if (el && el.offsetTop <= y) cur = g.label;
        })
      );
      setActive(cur);
    };
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, [isHome]);

  const anchor = (href) => (isHome ? href : `/${href}`);

  return (
    <header
      data-testid="site-nav"
      className={`sticky top-0 z-40 backdrop-blur-md transition-colors duration-300 ${
        scrolled
          ? "bg-[#faf8f5]/90 border-b border-[#e5e0d8]"
          : "bg-[#faf8f5]/60 border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16 h-16 flex items-center justify-between">
        <Link
          data-testid="nav-home-link"
          to="/"
          className="flex items-center gap-2 group"
        >
          <span
            aria-hidden
            className="w-2 h-2 rounded-full bg-[#d9a92e] pulse-dot"
          />
          <span className="font-display text-lg tracking-tight">
            Sinthu Nathan
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-7" onMouseLeave={() => setMenu(null)}>
          {GROUPS.map((g) => (
            <div key={g.label} className="relative" onMouseEnter={() => setMenu(g.label)}>
              <button
                type="button"
                data-testid={`nav-group-${g.label.toLowerCase()}`}
                onClick={() => setMenu((m) => (m === g.label ? null : g.label))}
                aria-expanded={menu === g.label}
                data-active={active === g.label ? "true" : "false"}
                className={`relative inline-flex items-center gap-1 text-sm transition-colors whitespace-nowrap py-2 after:absolute after:left-0 after:right-4 after:-bottom-0.5 after:h-[2px] after:bg-[#9a7218] after:origin-left after:transition-transform after:duration-300 ${
                  active === g.label ? "text-[#14352a] after:scale-x-100" : "text-[#55625b] hover:text-[#14352a] after:scale-x-0"
                }`}
              >
                {t(g.label)}
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${menu === g.label ? "rotate-180" : ""}`} />
              </button>
              {menu === g.label && (
                <div
                  data-testid={`nav-menu-${g.label.toLowerCase()}`}
                  className="absolute left-0 top-full pt-1 fade-up"
                >
                  <div className="min-w-[180px] bg-white border border-[#e5e0d8] shadow-xl rounded-xl p-1.5">
                    {g.links.map((l) => (
                      <a
                        key={l.href}
                        data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                        href={anchor(l.href)}
                        onClick={() => setMenu(null)}
                        className="block px-3 py-2 rounded-lg text-sm text-[#14352a] hover:bg-[#f2ece4] hover:text-[#9a7218] transition-colors"
                      >
                        {t(l.label)}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
          <a href={anchor("#contact")}>
            <Button
              data-testid="nav-contact-btn"
              className="btn-accent rounded-full px-5 h-9 text-sm"
            >
              {t("Contact")}
            </Button>
          </a>
          <LangSwitch lang={lang} setLang={setLang} />
        </nav>

        <div className="md:hidden flex items-center gap-2">
        <LangSwitch lang={lang} setLang={setLang} />
        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden h-9 w-9 grid place-items-center rounded-full border border-[#e5e0d8]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          <span className="block w-4 h-[1.5px] bg-[#14352a]" />
        </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="md:hidden border-t border-[#e5e0d8] bg-[#faf8f5]"
        >
          <div className="max-w-7xl mx-auto px-5 py-4 grid grid-cols-3 gap-4">
            {GROUPS.map((g) => (
              <div key={g.label}>
                <div className="font-mono-label text-[9px] text-[#9a7218] mb-2">{t(g.label)}</div>
                {g.links.map((l) => (
                  <a
                    key={l.href}
                    href={anchor(l.href)}
                    onClick={() => setOpen(false)}
                    className="block text-sm text-[#55625b] py-1"
                  >
                    {t(l.label)}
                  </a>
                ))}
              </div>
            ))}
            <a
              href={anchor("#contact")}
              onClick={() => setOpen(false)}
              className="col-span-3 text-sm font-medium text-[#9a7218] py-1"
            >
              {t("Contact →")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

function LangSwitch({ lang, setLang }) {
  return (
    <div data-testid="site-lang-toggle" className="inline-flex rounded-full border border-[#e5e0d8] overflow-hidden text-[10px] font-mono-label">
      {["en", "da"].map((l) => (
        <button
          key={l}
          data-testid={`site-lang-${l}`}
          onClick={() => setLang(l)}
          className={`px-2.5 h-7 ${lang === l ? "bg-[#14352a] text-white" : "text-[#55625b] hover:bg-[#f2ece4]"}`}
          aria-pressed={lang === l}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
